import React, { useState } from "react";
import { RotateCcw, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const COPY = {
  rejected: {
    label: "Reverse rejection & approve",
    icon: RotateCcw,
    title: "Reverse rejection and approve this document?",
    body: "This is an override, not a normal approval: the document is currently rejected. Approving it now counts towards the lawyer's required documents. Use this only if the rejection was made in error or the issue has been resolved.",
    action: "Reverse & approve",
    needsReason: false,
    cls: "border-amber-300 text-amber-800 hover:bg-amber-50",
  },
  approved: {
    label: "Revoke approval",
    icon: Undo2,
    title: "Revoke approval and reject this document?",
    body: "This is an override: the document is currently approved. Revoking marks it rejected and the reason below is shown to the lawyer. Existing verification state is not changed by this screen.",
    action: "Revoke & reject",
    needsReason: true,
    cls: "border-rose-300 text-rose-700 hover:bg-rose-50",
  },
};

export function KycOverrideButton({ item, busy, onConfirm }) {
  const copy = COPY[item.status];
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!copy) return null;
  const Icon = copy.icon;
  const kind = item.status === "rejected" ? "reverse" : "revoke";
  const disabled = copy.needsReason && reason.trim().length < 3;

  return (
    <>
      <Button
        variant="outline" size="sm" onClick={() => { setReason(""); setOpen(true); }}
        disabled={busy === `override-${item.id}`} data-testid={`kyc-${kind}-${item.id}`}
        className={`rounded-lg text-xs h-8 ${copy.cls}`}
      >
        <Icon size={12} className="mr-1" /> {copy.label}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`kyc-${kind}-dialog-${item.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy.body}</AlertDialogDescription>
          </AlertDialogHeader>
          {copy.needsReason && (
            <Textarea
              rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for revoking (shown to the lawyer)"
              data-testid={`kyc-${kind}-reason-${item.id}`} className="rounded-xl text-sm"
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`kyc-${kind}-cancel-${item.id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={disabled} data-testid={`kyc-${kind}-confirm-${item.id}`}
              onClick={() => { setOpen(false); onConfirm(item, reason.trim()); }}
              className={item.status === "rejected" ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-600 hover:bg-rose-700"}
            >
              {copy.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
